/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	root: {
		display: 'grid',
		gap: token('space.200'),
		gridTemplateColumns: '1fr',
		paddingBlock: token('space.200'),
		'@media (min-width: 30rem)': {
			gridTemplateColumns: '1fr 1fr',
		},
	},
	threeColumn: {
		'@media (min-width: 48rem)': {
			gridTemplateColumns: '1fr 1fr 1fr',
		},
	},
});

export type FeatureBlockGridProps = {
	children: ReactNode;
	/**
	 * Maximum number of columns at wide viewports. Defaults to 2.
	 */
	columns?: 2 | 3;
};

/**
 * __FeatureBlockGrid__
 *
 * A responsive, non-clickable content grid for laying out 3–9 peer concepts
 * (principles, building blocks, guidelines) as image + heading + description
 * blocks side by side. Use `ContentCardGroup` instead if items need to link.
 *
 * @example
 * ```tsx
 * <FeatureBlockGrid columns={3}>
 *   {items.map((item) => <FeatureBlock key={item.title} {...item} />)}
 * </FeatureBlockGrid>
 * ```
 */
export const FeatureBlockGrid: (props: FeatureBlockGridProps) => React.JSX.Element = ({
	children,
	columns = 2,
}) => {
	return <div css={[styles.root, columns === 3 && styles.threeColumn]}>{children}</div>;
};
